// Client-side leaderboard + nickname helpers.

import { api } from "../base";
import { TICK_RATE } from "../tickrate";

const NICK_KEY = "webrace.nickname";
const TOKEN_KEY = "webrace.token";

/** Get the stored nickname + token, or null if not registered. */
export function getIdentity(): { nickname: string; token: string } | null {
  try {
    const nickname = localStorage.getItem(NICK_KEY);
    const token = localStorage.getItem(TOKEN_KEY);
    if (nickname && token) return { nickname, token };
  } catch {
    /* localStorage unavailable */
  }
  return null;
}

/**
 * Register a nickname (or, when `token` is given, rename that existing identity
 * in place — keeping its leaderboard history). Stores the returned identity.
 */
export async function registerNickname(
  nickname: string,
  token?: string,
): Promise<{ nickname: string; token: string }> {
  const res = await fetch(api("/api/nickname"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(token ? { nickname, token } : { nickname }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error ?? `nickname failed (${res.status})`);
  }
  try {
    localStorage.setItem(NICK_KEY, data.nickname);
    localStorage.setItem(TOKEN_KEY, data.token);
  } catch {
    /* ignore */
  }
  return { nickname: data.nickname, token: data.token };
}

/** Base64-encode bytes (for the ghost upload). */
function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

/** Submit a finished race time (and optionally its ghost) to the leaderboard. */
export async function submitTime(
  token: string,
  map: string,
  timeMs: number,
  splits: number[],
  prejumped = false,
  ghost: Float32Array | null = null,
  ticket = "",
): Promise<void> {
  const body: Record<string, unknown> = { token, map, timeMs, splits, prejumped, tickRate: TICK_RATE };
  if (ghost && ghost.length > 0) {
    const bytes = new Uint8Array(ghost.buffer, ghost.byteOffset, ghost.byteLength);
    body.ghost = bytesToB64(bytes);
  }
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  // The game server issues a short-lived ticket after `hello`; the leaderboard
  // rejects direct anonymous submissions.
  if (ticket) headers["x-webrace-ticket"] = ticket;
  const res = await fetch(api("/api/times"), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    console.warn("submit time failed:", data.error ?? res.status);
  }
}

/**
 * Fetch a stored ghost as flat f32 pose samples (`[race, x, y, z, yaw, pitch]`).
 * `kind` is "wr" (overall best) or "pr" (your best; needs a token). Returns null
 * when unavailable.
 */
export async function fetchGhost(
  map: string,
  kind: "wr" | "pr",
  token?: string,
): Promise<Float32Array | null> {
  const q = new URLSearchParams({ map, kind });
  if (token) q.set("token", token);
  try {
    const res = await fetch(api(`/api/ghost?${q.toString()}`));
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    if (buf.byteLength === 0 || buf.byteLength % 24 !== 0) return null;
    return new Float32Array(buf);
  } catch {
    return null;
  }
}

export interface LeaderboardEntry {
  nickname: string;
  time_ms: number;
  prejumped?: number;
  /** Cumulative checkpoint tick counts (one per checkpoint). */
  splits?: number[];
}

export interface LeaderboardBoard {
  entries: LeaderboardEntry[];
  /** The caller's PB on this map (needs a token), even outside the top 100. */
  me: LeaderboardEntry | null;
}

/** Fetch the leaderboard for a map. `prejumped` is a 0/1 flag. */
export async function fetchLeaderboard(map: string): Promise<LeaderboardEntry[]> {
  const res = await fetch(api(`/api/leaderboard?map=${encodeURIComponent(map)}`));
  const data = await res.json();
  return data.entries ?? [];
}

/**
 * Leaderboard + checkpoint splits, plus the caller's PB (`me`) when a token is
 * supplied. Returns null on any failure so the HUD can stay hidden instead of
 * inventing a rank.
 */
export async function fetchLeaderboardBoard(
  map: string,
  token?: string,
): Promise<LeaderboardBoard | null> {
  const q = new URLSearchParams({ map });
  if (token) q.set("token", token);
  try {
    const res = await fetch(api(`/api/leaderboard?${q.toString()}`), { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      entries: Array.isArray(data.entries) ? data.entries : [],
      me: data.me ?? null,
    };
  } catch {
    return null;
  }
}

export type ProfileRating = {
  mode: string;
  rating: number;
  rd: number;
  volatility: number;
  games: number;
  updated_at: number;
};

export type ProfileMatch = {
  id: string;
  mode: string;
  map: string;
  teams: number;
  winner_team: number;
  score: string | null;
  created_at: number;
  team: number;
  player_score: number;
  place: number;
  rating_before: number | null;
  rating_after: number | null;
  rd_after: number | null;
};

/** Fetch the caller's per-mode ratings and recent match history. */
export async function fetchProfile(
  token: string,
): Promise<{ nickname: string; ratings: ProfileRating[]; matches: ProfileMatch[] } | null> {
  try {
    const res = await fetch(api(`/api/profile?token=${encodeURIComponent(token)}`));
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}
